// Pure kanban parsing, sanitization, and prompt-rendering helpers.
//
// This is the leaf half of the manager's kanban subsystem: template-id
// classification, LLM-output parsing (standup / plan / kanban-update), string
// sanitization, board-item predicates, and board-for-prompt rendering. Every
// function here is pure (or, for getBuiltInSkillContent, depends only on the
// filesystem) — none touch the API client, tmux, the persistent session, or any
// other manager module — so the whole module is importable by the scheduler,
// the inject/harvest path, and `manager-worker.ts` itself without creating an
// import cycle. The stateful kanban code (maybeInjectKanbanCheck, harvest,
// cron-monitor) stays in manager-worker.ts and imports the pieces it needs from
// here. See ADR-0011.

import { existsSync, readFileSync } from 'node:fs';
import { join } from 'node:path';

// Shape of a kanban board item as returned by /host/my-kanban. Shared between
// the parsers/predicates here and the stateful kanban code in manager-worker.ts.
// ENG-5769: source_integration / source_external_id carry the originating
// channel + thread coordinates captured by `kanban_add` (ENG-5382). They flow
// through the kanban-work nudge so the agent resumes a card knowing which
// Slack/Telegram thread to reply in — previously only CLAUDE.md's Active
// Tasks section had them, and that section was gated behind a default-off
// env flag.
export type BoardItem = { id: string; title: string; status: string; priority: number; estimated_minutes?: number; deliverable?: string; result?: string; notify_channel?: string; notify_to?: string; updated_at?: string; source_type?: string; source_ref?: string; source_integration?: string | null; source_external_id?: string | null; source_url?: string | null };

// Template IDs that map to agent field updates
export const STANDUP_TEMPLATES = new Set(['daily-standup', 'end-of-day-summary']);
export const TASK_UPDATE_TEMPLATES = new Set(['hourly-status', 'task-update']);
export const PLAN_TEMPLATES = new Set(['morning-plan']);
export const KANBAN_WORK_TEMPLATES = new Set(['kanban-work']);
// ENG-7385: templates that deliver their result by CALLING A TOOL, not by having
// the manager message the card result. The weekly performance review runs the
// `submit_performance_review` tool (which DMs the agent's manager the rating
// form); its card is only a working record. These must NOT get the generic
// "write-to-card / don't message anyone" delivery contract, which contradicts the
// tool call and made agents file a self-review card while the manager got nothing.
// (Value mirrors PERFORMANCE_REVIEW_TEMPLATE_ID in @augmented/core.)
export const TOOL_DELIVERED_TEMPLATES = new Set(['performance-review']);

// ENG-5599 (S1 of ENG-5576): a "plain" scheduled task is one whose claude -p
// result only needs delivering — no template-specific side effects in
// processClaudeTaskResult (standup posts to /host/agent-status; plan /
// task-update write board items) and not the kanban-work loop. Only plain
// tasks are eligible for the in-session kanban route in S1; the rest keep the
// oneshot until their side effects are re-homed in-session (S3, ENG-5601).
export function isPlainScheduledTemplate(templateId: string): boolean {
  return (
    !STANDUP_TEMPLATES.has(templateId) &&
    !TASK_UPDATE_TEMPLATES.has(templateId) &&
    !PLAN_TEMPLATES.has(templateId) &&
    !KANBAN_WORK_TEMPLATES.has(templateId)
  );
}

// ENG-6546 (S6 of ENG-5576): templates whose side effect is applied MANAGER-side
// by parsing the done card's result and POSTing it — the standup + task-update
// families, both of which write to `POST /host/agent-status` (an idempotent
// UPSERT of the agent's standup / current_tasks fields). These are now eligible
// to route in-session: the side effect runs in deliverScheduledCardResult on the
// agent's self-completion, where before they were forced onto the `claude -p`
// oneshot. Because the post is an idempotent overwrite, a delivery retry re-posts
// the same status harmlessly — no double-apply. morning-plan is deliberately
// EXCLUDED: its side effect (creating board cards) is a non-idempotent INSERT, so
// it is re-homed by having the in-session agent call kanban_add itself rather
// than the manager parsing (ENG-6546 step 2). kanban-work never routes here.
export function isManagerSideEffectTemplate(templateId: string): boolean {
  return STANDUP_TEMPLATES.has(templateId) || TASK_UPDATE_TEMPLATES.has(templateId);
}

// ENG-5435 + ENG-5662: manager-gated hybrid kanban-work injection is now the
// SOLE kanban-work mechanism. The two predecessors it replaced — the external
// `claude -p kanban-work` cron (ENG-5405) and the in-session `/loop kanban-work`
// (ENG-5404) — have been removed, along with their AGT_DISABLE_KANBAN_WORK_CRON /
// AGT_DISABLE_KANBAN_LOOP kill switches and the AGT_KANBAN_HYBRID opt-in flag.
// Kept as a function (rather than inlining `true`) so the step-7e poll block and
// its call sites read unchanged. The trigger lives in step 7e of the per-agent
// poll; helpers below.
export function isKanbanHybridEnabled(): boolean {
  return true;
}

export function isKanbanHybridDryRun(): boolean {
  const v = process.env['AGT_KANBAN_HYBRID_DRY_RUN'];
  return v === '1' || v?.toLowerCase() === 'true';
}

// Same status set as ACTIONABLE_STATUSES (both exclude `backlog` as of
// ENG-5605 — backlog is curatorial, not "pick this up now"; the board owner
// promotes backlog → todo when ready). The hybrid path's *additional* narrowing
// over the plain kanban-work gate is the scheduled_task exclusion in
// isHybridActionable below, not the status set.
export const HYBRID_ACTIONABLE_STATUSES = new Set(['todo', 'in_progress']);

// ENG-5600: a board item the generic hybrid nudge should act on. Excludes
// scheduled_task cards — those are driven by routeScheduledTaskViaKanban's own
// per-card nudge + run (ENG-5599). Letting the board-level nudge also fire for
// them opens a competing source_type='kanban' run whose marker would absorb the
// card's turns, mis-attributing the scheduled task's cost (and double-nudging).
export function isHybridActionable(item: BoardItem): boolean {
  return HYBRID_ACTIONABLE_STATUSES.has(item.status) && item.source_type !== 'scheduled_task';
}

export function hasHybridActionableItems(items: BoardItem[]): boolean {
  return items.some(isHybridActionable);
}

// Slash commands go via tmux send-keys directly (and carry no marker);
// plain-text nudges (e.g. the kanban check) go through injectMessage with a
// run marker appended.
export function isSlashCommand(command: string): boolean {
  return command.trimStart().startsWith('/');
}

export const BOARD_INJECT_TEMPLATES = new Set(['morning-plan', 'task-update', 'hourly-status', 'end-of-day-summary']);

// ENG-4410: Check if the board has work the kanban-work loop will actually act
// on, used to skip the kanban-work claude -p spawn when there's nothing to do.
// ENG-5605: `backlog` is deliberately EXCLUDED. The kanban-work loop only works
// `todo`/`in_progress` cards (it promotes the top `todo`); a card parked in
// `backlog` is curatorial, not ready. Counting backlog here made an agent with
// backlog cards spawn claude -p every ~5 min only to return `<no-delivery/>` —
// no-op subprocess spawns re-paying the full prompt-cache prefix for zero work
// (observed on maven, agt-aws-1). This now matches HYBRID_ACTIONABLE_STATUSES.
export const ACTIONABLE_STATUSES = new Set(['todo', 'in_progress']);
export function hasActionableItems(items: BoardItem[]): boolean {
  return items.some((item) => ACTIONABLE_STATUSES.has(item.status));
}

export function parseStandupSummary(summary: string): { yesterday: string; today: string; blockers: string } {
  // Try to extract sections from the standup text
  const lines = summary.split('\n');
  let yesterday = '';
  let today = '';
  let blockers = '';
  let currentSection: 'yesterday' | 'todo' | 'blockers' | null = null;

  for (const line of lines) {
    const lower = line.toLowerCase();
    if (lower.includes('yesterday') || lower.includes('accomplished')) {
      currentSection = 'yesterday';
      continue;
    } else if (lower.includes('today') || lower.includes('todo') || lower.includes('working on')) {
      // `today` is checked AFTER the yesterday/accomplished branch above, so a
      // "Today:" header maps to the todo section while "yesterday" (which has no
      // `today` substring) still wins its own branch. CodeRabbit PR #2140.
      currentSection = 'todo';
      continue;
    } else if (lower.includes('blocker')) {
      currentSection = 'blockers';
      continue;
    }

    const trimmed = line.replace(/^[-*•]\s*/, '').trim();
    if (!trimmed) continue;

    switch (currentSection) {
      case 'yesterday': yesterday += (yesterday ? '\n' : '') + trimmed; break;
      case 'todo': today += (today ? '\n' : '') + trimmed; break;
      case 'blockers': blockers += (blockers ? '\n' : '') + trimmed; break;
    }
  }

  // Fallback: if parsing didn't find sections, use full summary
  if (!yesterday && !today && !blockers) {
    today = summary;
  }

  return { yesterday, today, blockers };
}

export function parsePlanItems(summary: string): Array<{
  title: string;
  description?: string;
  priority: number;
  estimated_minutes?: number;
  status: string;
}> {
  const items: Array<{
    title: string;
    description?: string;
    priority: number;
    estimated_minutes?: number;
    status: string;
  }> = [];

  const lines = summary.split('\n');
  let currentItem: (typeof items)[0] | null = null;

  for (const line of lines) {
    const trimmed = line.trim();

    // Match numbered/bulleted items: "1. [HIGH] Task title (~30min)" or "- [MEDIUM] Task (~2hr)"
    const itemMatch = trimmed.match(/^(?:\d+[\.\)]\s*|[-*•]\s*)\[?(HIGH|MEDIUM|LOW|MED)\]?\s*(.+)/i);

    if (itemMatch) {
      // Push previous item
      if (currentItem) items.push(currentItem);

      const priorityStr = itemMatch[1]!.toUpperCase();
      const rest = itemMatch[2]!;

      // Extract time estimate
      let estimatedMinutes: number | undefined;
      const timeMatch = rest.match(/\(~?(\d+)\s*(min(?:utes?)?|hr?(?:ours?)?|h)\)/i);
      if (timeMatch) {
        const val = parseInt(timeMatch[1]!, 10);
        const unit = timeMatch[2]!.toLowerCase();
        estimatedMinutes = unit.startsWith('h') ? val * 60 : val;
      }

      // Title is everything except the time estimate — sanitize LLM output
      const title = sanitizeKanbanString(
        rest.replace(/\(~?\d+\s*(?:min(?:utes?)?|hr?(?:ours?)?|h)\)/i, ''),
        MAX_KANBAN_TITLE_LENGTH,
      );
      if (!title) continue;

      const priorityMap: Record<string, number> = { HIGH: 1, MEDIUM: 2, MED: 2, LOW: 3 };
      const priority = priorityMap[priorityStr] ?? 2;
      if (![1, 2, 3].includes(priority)) continue;

      currentItem = {
        title,
        priority,
        estimated_minutes: estimatedMinutes,
        status: 'todo',
      };
    } else if (currentItem && trimmed && !trimmed.match(/^(?:PLAN|---)/i)) {
      // Non-numbered line after a plan item = description (length-limited)
      const descLine = sanitizeKanbanString(trimmed, MAX_KANBAN_NOTES_LENGTH);
      currentItem.description = currentItem.description
        ? sanitizeKanbanString(currentItem.description + '\n' + descLine, MAX_KANBAN_NOTES_LENGTH)
        : descLine;
    }
  }

  if (currentItem) items.push(currentItem);
  return items;
}

export const VALID_KANBAN_STATUSES = new Set(['backlog', 'todo', 'in_progress', 'done']);
const MAX_KANBAN_TITLE_LENGTH = 500;
const MAX_KANBAN_NOTES_LENGTH = 2000;

/** Sanitize a string from LLM output: trim + length limit */
export function sanitizeKanbanString(value: string, maxLen: number): string {
  if (!value) return '';
  return value
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '') // strip control chars (keep \n, \r, \t)
    .replace(/\{\{.*?\}\}/g, '')                          // strip template injection markers
    .replace(/^(System|Assistant|Human):\s*/gmi, '')      // strip prompt-role prefixes
    .replace(/#{2,}/g, '')                                // strip markdown heading markers
    .replace(/\s+/g, ' ')                                 // collapse whitespace
    .trim()
    .slice(0, maxLen);
}

/** Sanitize all external fields on a board item before caching / prompt injection */
export function sanitizeBoardItem<T extends { title: string; status: string; deliverable?: string }>(item: T): T {
  return {
    ...item,
    title: sanitizeKanbanString(item.title, 200),
    status: sanitizeKanbanString(item.status, 50),
    ...(item.deliverable ? { deliverable: sanitizeKanbanString(item.deliverable, 500) } : {}),
  };
}

// ---------------------------------------------------------------------------
// Built-in skill files — bundled for auto-deployment to all agents
// ---------------------------------------------------------------------------

const builtInSkillCache = new Map<string, Array<{ relativePath: string; content: string }> | null>();

export function getBuiltInSkillContent(skillId: string): Array<{ relativePath: string; content: string }> | null {
  if (builtInSkillCache.has(skillId)) return builtInSkillCache.get(skillId)!;

  try {
    // Resolve skill directory relative to the CLI package root
    // In dev: skills/<skillId>/SKILL.md is in the monorepo root
    // When bundled: skills are embedded at build time or read from cwd
    //
    // The import.meta.url climb is dev-only (in the tsup bundle import.meta.url
    // points at dist/, where the first cwd-relative candidate is the one that
    // resolves). This module lives at apps/cli/src/lib/manager/kanban/, so six
    // `..` segments reach the monorepo root — two more than manager-worker.ts's
    // original four, accounting for the manager/kanban/ nesting (ADR-0011 move).
    const candidates = [
      join(process.cwd(), 'skills', skillId, 'SKILL.md'),
      join(new URL('.', import.meta.url).pathname, '..', '..', '..', '..', '..', '..', 'skills', skillId, 'SKILL.md'),
    ];

    for (const candidate of candidates) {
      if (existsSync(candidate)) {
        const content = readFileSync(candidate, 'utf-8');
        const files = [{ relativePath: 'SKILL.md', content }];
        builtInSkillCache.set(skillId, files);
        return files;
      }
    }

    builtInSkillCache.set(skillId, null);
    return null;
  } catch {
    builtInSkillCache.set(skillId, null);
    return null;
  }
}

// ---------------------------------------------------------------------------

export function parseKanbanUpdates(summary: string): Array<{
  title: string;
  status: string;
  notes?: string;
  result?: string;
}> {
  const updates: Array<{ title: string; status: string; notes?: string; result?: string }> = [];

  // Look for the "KANBAN UPDATE:" section. Case-insensitive so LLM output that
  // varies the casing ("Kanban Update:", "kanban update:") still parses — the
  // section start is the matched index + the matched token's length. CodeRabbit
  // PR #2140.
  const markerMatch = /kanban update:/i.exec(summary);
  if (!markerMatch) return updates;

  const kanbanSection = summary.slice(markerMatch.index + markerMatch[0].length);
  const lines = kanbanSection.split('\n');

  for (const line of lines) {
    const trimmed = line.trim();
    // Match: - "item title": status (optional notes/result)
    // Accept legacy 'today' as a synonym for 'todo' so prompts that haven't
    // been re-rendered post-rename still parse cleanly. Map to 'todo' before
    // validating against VALID_KANBAN_STATUSES (which only contains 'todo').
    const match = trimmed.match(/^[-*•]\s*"([^"]+)":\s*(backlog|todo|today|in_progress|done)(?:\s*\((.+)\))?/i);
    if (match) {
      const rawStatus = match[2]!.toLowerCase();
      const status = rawStatus === 'today' ? 'todo' : rawStatus;
      // Validate status is in the allowed set
      if (!VALID_KANBAN_STATUSES.has(status)) continue;

      const title = sanitizeKanbanString(match[1]!, MAX_KANBAN_TITLE_LENGTH);
      if (!title) continue;

      const parenthetical = match[3] ?? undefined;

      // Extract result from parenthetical when status is done
      // Pattern: "result: <value>" or just the whole parenthetical as notes
      let notes: string | undefined;
      let result: string | undefined;

      if (parenthetical && status === 'done') {
        const resultMatch = parenthetical.match(/^result:\s*(.+)/i);
        if (resultMatch) {
          result = sanitizeKanbanString(resultMatch[1]!, MAX_KANBAN_NOTES_LENGTH);
        } else {
          notes = sanitizeKanbanString(parenthetical, MAX_KANBAN_NOTES_LENGTH);
        }
      } else if (parenthetical) {
        notes = sanitizeKanbanString(parenthetical, MAX_KANBAN_NOTES_LENGTH);
      }

      updates.push({
        title,
        status,
        notes,
        result,
      });
    }
  }

  return updates;
}

/**
 * ENG-5769: render the kanban board for prompt injection.
 *
 * Why the source coords matter: after ENG-5382, `kanban_add` auto-captures
 * the originating Slack/Telegram thread on the card (DB columns
 * `source_integration` / `source_external_id`, public-shape names
 * `source_channel` / `source_thread_id`). When a kanban-work nudge picks
 * the card back up post-restart, the agent needs those coordinates to
 * reply in the thread that asked the question — not the default channel.
 *
 * Prior to ENG-5769 this function rendered only title/priority/estimate/
 * deliverable, so the agent silently lost thread continuity on resume
 * unless `AGT_ACTIVE_TASKS_INJECT=1` (the default-off CLAUDE.md path) was
 * on. Now todo + in_progress rows include an inline `source:` sub-line
 * when coordinates are present, and the trailing guidance tells the
 * agent to reply in that thread on completion.
 *
 * Exported so the source-coord rendering can be unit-tested without
 * standing up the full manager-worker poll loop.
 */
export function formatBoardForPrompt(
  items: Array<{
    title: string;
    status: string;
    priority: number;
    estimated_minutes?: number;
    deliverable?: string;
    source_integration?: string | null;
    source_external_id?: string | null;
    source_url?: string | null;
  }>,
  template: 'morning-plan' | 'follow-up',
): string {
  if (items.length === 0) return '';

  const priorityLabel = (p: number) => p === 1 ? 'HIGH' : p === 3 ? 'LOW' : 'MED';
  const timeLabel = (m?: number) => m ? ` (~${m >= 60 ? `${Math.round(m / 60)}hr` : `${m}min`})` : '';
  const deliverableLine = (d?: string) => d ? `\n     Deliverable: ${d}` : '';
  // Source rendering mirrors `buildActiveTasksSection` (claudecode/identity.ts)
  // so both surfaces stay consistent (AC #3). A row with channel+thread reads
  // as `slack thread C123:1700000000.1`; a row with only a URL falls back to
  // the URL alone (rare — older rows from before ENG-5382's universal capture).
  const sourceLine = (
    channel?: string | null,
    thread?: string | null,
    url?: string | null,
  ): string => {
    const parts: string[] = [];
    if (channel && thread) parts.push(`${channel} thread ${thread}`);
    if (url) parts.push(url);
    return parts.length > 0 ? `\n     source: ${parts.join(' • ')}` : '';
  };

  const grouped: Record<string, typeof items> = {};
  for (const item of items) {
    const key = item.status;
    if (!grouped[key]) grouped[key] = [];
    grouped[key]!.push(item);
  }

  // Whether any actionable card carries a source thread — drives the
  // "reply in the originating thread" guidance below. No false positives
  // for boards that are all internal tasks.
  const hasSourceThread = items.some(
    (i) =>
      (i.status === 'todo' || i.status === 'in_progress') &&
      !!i.source_integration &&
      !!i.source_external_id,
  );

  const lines: string[] = [];

  if (template === 'morning-plan') {
    lines.push('=== CURRENT BOARD ===');

    for (const [status, label] of [['backlog', 'BACKLOG (carry-over)'], ['todo', 'TO DO'], ['in_progress', 'IN PROGRESS']] as const) {
      const statusItems = grouped[status];
      if (statusItems && statusItems.length > 0) {
        lines.push(`${label}:`);
        statusItems.forEach((item, i) => {
          lines.push(`  ${i + 1}. [${priorityLabel(item.priority)}] ${item.title}${timeLabel(item.estimated_minutes)}${deliverableLine(item.deliverable)}${sourceLine(item.source_integration, item.source_external_id, item.source_url)}`);
        });
      }
    }

    lines.push('=====================');
    lines.push('');
    lines.push('Create today\'s plan. You may:');
    lines.push('- Move backlog items to "todo"');
    lines.push('- Add new items you\'ve identified');
    lines.push('- Reprioritise existing items');
    lines.push('');
  } else {
    lines.push('=== YOUR KANBAN BOARD ===');

    for (const [status, label] of [['todo', 'TO DO'], ['in_progress', 'IN PROGRESS'], ['backlog', 'BACKLOG']] as const) {
      const statusItems = grouped[status];
      if (statusItems && statusItems.length > 0) {
        lines.push(`${label}:`);
        statusItems.forEach((item, i) => {
          lines.push(`  ${i + 1}. [${priorityLabel(item.priority)}] ${item.title}${timeLabel(item.estimated_minutes)}${deliverableLine(item.deliverable)}${sourceLine(item.source_integration, item.source_external_id, item.source_url)}`);
        });
      }
    }

    const doneItems = grouped['done'];
    if (doneItems && doneItems.length > 0) {
      lines.push('DONE TODAY:');
      doneItems.forEach((item, i) => {
        lines.push(`  ${i + 1}. ${item.title}`);
      });
    }

    lines.push('=========================');
    lines.push('');
    lines.push('IMPORTANT: Use kanban MCP tools to update the board IN REAL TIME:');
    lines.push('1. FIRST call kanban_move to move your chosen item to in_progress BEFORE starting work');
    lines.push('2. Do the work');
    lines.push('3. Call kanban_done with a result summary when finished');
    lines.push('4. If blocked, call kanban_update with notes, then pick the next item');
    lines.push('');
    if (hasSourceThread) {
      // ENG-5769: thread-continuity guidance is conditional so boards
      // without channel-sourced cards don't carry instructions for a
      // case that can't apply.
      lines.push('THREAD CONTINUITY: If a card shows a `source:` line, the request originated');
      lines.push('in that Slack/Telegram thread. When you deliver the result, reply in that');
      lines.push('thread (not the default channel) so the user sees the answer where they');
      lines.push('asked. The card\'s `source_channel` + `source_thread_id` are the thread');
      lines.push('coordinates to use.');
      lines.push('');
    }
    lines.push('SELF-MANAGEMENT: When you receive a request from a channel (Slack, Telegram)');
    lines.push('that takes more than a quick response, create a task first with kanban_add,');
    lines.push('move to in_progress, do the work, then kanban_done with a result summary.');
    lines.push('');
    lines.push('If MCP tools are unavailable, include a KANBAN UPDATE section in your output:');
    lines.push('KANBAN UPDATE:');
    lines.push('- "item title": new_status (optional notes)');
    lines.push('- "item title": done (result: <what you produced>)');
    lines.push('Statuses: backlog, today, in_progress, done');
    lines.push('');
  }

  return lines.join('\n');
}
